/**
 * ScreenplayDocument elements → Fountain text. PURE module (no React, no
 * Lexical), the write-side partner of fountain.js's parseFountain.
 *
 * ⚠ MATCHED PAIR: this serializer is written against parseFountain's exact
 * classification cascade (fountain.js) and they MUST change together — every
 * emission rule here exists to make a specific parser branch re-produce the
 * element it came from. The round-trip suite in fountainSerializer.test.js
 * pins the pairing.
 *
 * The contract (the export-side mirror of import's "nothing is silently
 * dropped"):
 *
 *  1. ROUND-TRIP — for any element sequence parseFountain itself can
 *     produce, parseFountain(serializeFountain(elements)) reproduces the
 *     exact same { type, text } sequence. Forced markers (@ . > !) are used
 *     whenever a plain emission would let a heuristic reclassify the line.
 *
 *  2. TEXT IS NEVER INVENTED OR DROPPED — the serializer never adds, removes
 *     or rewrites a character of element text to make it round-trip. When a
 *     structure has no Fountain representation (see notes below), the TEXT
 *     still survives verbatim; only the TYPE or element-boundary degrades,
 *     and every such degradation is counted in the returned `notes`.
 *
 *  3. HONEST NOTES — `notes` counts everything a reimport of this file
 *     would classify or render differently than the document it came from:
 *       - shots (Fountain has no shot construct → forced action)
 *       - orphan dialogue/parentheticals (no preceding character chain —
 *         Fountain's dialogue is contextual, an orphan cannot be expressed)
 *       - adjacent dialogue elements (reimport joins them into one)
 *       - text containing Fountain markup lookalikes (emphasis pairs,
 *         [[ ]], scene numbers in headings…) that reimport would strip —
 *         our parser has no escape syntax, so these cannot be shielded
 *
 * Uppercasing is presentation (CSS/PDF) and is NOT applied here — text is
 * exported exactly as stored, with forced markers carrying the semantics
 * (a lowercase cue exports as "@amara" and round-trips verbatim).
 */
import { HEADING_PREFIX_RE, completeSceneHeadingPrefix, COMMON_TRANSITIONS } from './transitions';

/* Mirrors of parseFountain's helpers — tiny and duplicated deliberately so
 * this module stays import-light; the round-trip suite catches any drift. */
// Keep this range identical to fountain.js — Latin Extended Additional
// (U+1E00–U+1EFF) carries Yorùbá/Igbo cues (ỌṢỌ, ṄNAMDỊ). If only one side
// knows about them the round trip drifts: the parser reads the cue but the
// serializer no longer recognises it as all-caps and force-marks it with `@`.
const HAS_LATIN_LETTER_RE = /[A-Za-zÀ-ɏḀ-ỿ]/;
function isAllCapsLine(text) {
  if (!HAS_LATIN_LETTER_RE.test(text)) return false;
  return text === text.toUpperCase() && text !== text.toLowerCase();
}
function endsLikeASentence(text) {
  return /[.!?]$/.test(text) || /\.\.\.$/.test(text);
}

// Note/boneyard pair patterns — shared by the per-element check and the
// whole-document cross-element sweep below (the parser strips these over
// the WHOLE source before line-splitting, so a pair can span elements).
const NOTE_PAIR_RE = /\[\[[\s\S]*?\]\]/g;
const BONEYARD_PAIR_RE = /\/\*[\s\S]*?\*\//g;

/** Would reimport strip or rewrite characters inside this text? (Emphasis
 * pairs and paired note/boneyard markers are stripped by parseFountain, a
 * lone \r is normalized to \n, and our parser deliberately has no escape
 * syntax — see module doc note 3.) NB: fresh non-global regexes here —
 * .test() on a /g regex is stateful across calls. */
function reimportWouldAlter(text) {
  return (
    /\*{1,3}[^*]+\*{1,3}/.test(text) ||
    /_[^_]+_/.test(text) ||
    /\[\[[\s\S]*?\]\]/.test(text) ||
    /\/\*[\s\S]*?\*\//.test(text) ||
    text.includes('\r')
  );
}

function countPairs(text, re) {
  return (text.match(re) || []).length;
}

/** Whitespace the parser trims on every line — leading/trailing space in
 * stored text cannot survive a reimport. */
function hasUntrimmableWhitespace(text) {
  return text
    .split('\n')
    .some((line) => line !== line.trim());
}

/**
 * Would this line, emitted PLAIN as its own paragraph, re-classify as
 * something other than default action? Mirrors the cascade order in
 * parseFountain. `followerNonBlank` matters only for the character-cue
 * heuristic (immediate-next-line lookahead).
 */
function plainLineMisclassifies(line, followerNonBlank) {
  if (line === '') return true; // blank line = element skipped entirely
  if (/^={3,}$/.test(line)) return true; // page break
  if (/^#{1,6}(\s|$)/.test(line)) return true; // section
  if (line.startsWith('=')) return true; // synopsis
  if (line.startsWith('.') && line[1] !== '.') return true; // forced heading
  if (HEADING_PREFIX_RE.test(line)) return true; // standard heading
  if (line.startsWith('>')) return true; // forced transition / centered
  if (line.startsWith('@')) return true; // forced character
  if (line.startsWith('!')) return true; // forced action (marker would be eaten)
  if (line.startsWith('~')) return true; // lyric
  if (isAllCapsLine(line) && (line.endsWith('TO:') || COMMON_TRANSITIONS.includes(line))) {
    return true; // standard transition
  }
  if (isAllCapsLine(line) && followerNonBlank && !endsLikeASentence(line)) {
    return true; // standard character cue
  }
  return false;
}

function freshNotes() {
  return {
    // TYPE degradations — Fountain cannot express these structures.
    shotsAsAction: 0, // shot → forced action (reimports as action)
    orphanDialogue: 0, // dialogue with no character chain → forced action
    orphanParentheticals: 0, // same, for parentheticals
    malformedParentheticals: 0, // parenthetical text not "( … )"-shaped, or multi-line
    emptyInChain: 0, // empty dialogue/parenthetical inside a chain (blank line would end the block)
    unexpressibleHeadings: 0, // heading text itself starting with "." — the force marker can't attach
    unexpressibleTransitions: 0, // transition text ending in "<" — forced ">" form would read as centered
    dualMarkerNames: 0, // character name ending in "^" — reimport eats it as the dual-dialogue marker

    // STRUCTURE degradations — boundaries move on reimport, text survives.
    adjacentDialogueMerged: 0, // dialogue,dialogue → one joined element on reimport
    actionSplitByCollision: 0, // multi-line action whose first line needed "!" — reimports as 2 elements
    blankLineInText: 0, // embedded blank line splits the element on reimport

    // TEXT alterations a reimport would make (no escape syntax exists).
    textAlteredOnReimport: 0, // emphasis/note/boneyard lookalikes stripped, whitespace trimmed
    sceneNumberStripped: 0, // "#…#" tail on a heading — reimport strips it
  };
}

/**
 * serializeFountain(elements, { title, author }) → { text, notes }
 *
 * `elements`: [{ type, text }] (ids ignored — Fountain has no id concept;
 * reimport mints fresh ones, per the Part 1 design).
 * `title`/`author` (optional): emitted as a real Fountain title page. Also
 * naturally shields a first element shaped like "Title: …" from title-page
 * capture on reimport (body no longer starts at line 0).
 */
export function serializeFountain(elements, { title, author } = {}) {
  if (!Array.isArray(elements)) {
    throw new TypeError('serializeFountain: elements must be an array');
  }
  const notes = freshNotes();
  const out = []; // paragraphs; joined with blank lines at the end

  const noteTextAlterations = (text) => {
    if (reimportWouldAlter(text) || hasUntrimmableWhitespace(text)) {
      notes.textAlteredOnReimport += 1;
    }
  };

  // ── Title page ──────────────────────────────────────────────────────
  // \r included: a lone \r in a title would survive here, then the parser's
  // line normalization would split the title mid-line and inject a phantom
  // body element on reimport.
  const titleLines = [];
  if (title) titleLines.push(`Title: ${String(title).replace(/\r\n?|\n/g, ' ')}`);
  if (author) titleLines.push(`Author: ${String(author).replace(/\r\n?|\n/g, ' ')}`);
  if (titleLines.length > 0) out.push(titleLines.join('\n'));

  // ── Element emission ────────────────────────────────────────────────
  // Dialogue-chain state: a `character` opens a chain; `dialogue` and
  // `parenthetical` extend it (they are appended to the OPEN block, not
  // emitted as separate paragraphs); anything else closes it.
  let openBlock = null; // array of lines belonging to the current cue block
  let openBlockOnlyCue = false; // no dialogue/parenthetical line landed yet
  let lastLineOfBlockWasDialogue = false;

  const closeBlock = () => {
    if (openBlock) {
      // A plain cue was emitted on the assumption its chain would follow
      // immediately. If the block closes while the cue is still ALONE (the
      // chain member turned out unexpressible — empty dialogue, malformed
      // parenthetical), that assumption broke: a plain all-caps line with a
      // blank after it reimports as ACTION. Retro-force the marker so the
      // cue stays a cue.
      if (openBlockOnlyCue && !openBlock[0].startsWith('@')) {
        openBlock[0] = `@${openBlock[0]}`;
      }
      out.push(openBlock.join('\n'));
      openBlock = null;
      openBlockOnlyCue = false;
      lastLineOfBlockWasDialogue = false;
    }
  };

  for (let i = 0; i < elements.length; i += 1) {
    const el = elements[i];
    const text = el.text;
    const next = elements[i + 1];
    const nextExtendsChain =
      next && (next.type === 'dialogue' || next.type === 'parenthetical');

    switch (el.type) {
      case 'scene_heading': {
        closeBlock();
        noteTextAlterations(text);
        if (/#[A-Za-z0-9.]+#\s*$/.test(text)) notes.sceneNumberStripped += 1;
        const single = !text.includes('\n');
        if (single && text.startsWith('.')) {
          // "." can't attach (".." reads as ellipsis-guarded) — emit as
          // forced action; type degrades, text survives.
          out.push(`!${text}`);
          notes.unexpressibleHeadings += 1;
        } else if (single && HEADING_PREFIX_RE.test(text) && !completeSceneHeadingPrefix(text)) {
          // Canonical standard heading — plain emission round-trips.
          out.push(text);
        } else if (single) {
          // Unrecognized (or non-canonical) prefix — force it. A
          // non-canonical recognized prefix will be canonicalized on
          // reimport, same as the editor does on first edit.
          if (completeSceneHeadingPrefix(text)) notes.textAlteredOnReimport += 1;
          out.push(`.${text}`);
        } else {
          // Multi-line heading: Fountain can't express it — forced-action
          // lines preserve the text; type degrades.
          emitTextAsActionLines(text, out, notes);
          notes.unexpressibleHeadings += 1;
        }
        break;
      }

      case 'action': {
        closeBlock();
        noteTextAlterations(text);
        emitTextAsActionLines(text, out, notes);
        break;
      }

      case 'shot': {
        closeBlock();
        noteTextAlterations(text);
        // Part 1's mapping table already maps Fountain→shot as "no such
        // construct" — the reverse degrades symmetrically.
        emitTextAsActionLines(text, out, notes);
        notes.shotsAsAction += 1;
        break;
      }

      case 'transition': {
        closeBlock();
        noteTextAlterations(text);
        const single = !text.includes('\n');
        if (!single || text.endsWith('<')) {
          // ">" form would read as centered text — unexpressible.
          emitTextAsActionLines(text, out, notes);
          notes.unexpressibleTransitions += 1;
        } else if (
          isAllCapsLine(text) &&
          (text.endsWith('TO:') || COMMON_TRANSITIONS.includes(text)) &&
          !text.startsWith('>') &&
          !plainStartsAsEarlierBranch(text)
        ) {
          out.push(text); // standard transition — plain round-trips
        } else {
          out.push(`> ${text}`); // forced (parser trims after the marker)
        }
        break;
      }

      case 'character': {
        closeBlock();
        noteTextAlterations(text);
        const single = !text.includes('\n');
        if (!single) {
          emitTextAsActionLines(text, out, notes);
          notes.unexpressibleHeadings += 1; // multi-line cue: same class of degradation
          break;
        }
        if (text.endsWith('^')) notes.dualMarkerNames += 1; // marker gets eaten either way
        const plainSafe =
          nextExtendsChain && // cue heuristic needs an IMMEDIATE non-blank follower
          isAllCapsLine(text) &&
          !endsLikeASentence(text) &&
          !text.endsWith('^') &&
          !plainStartsAsEarlierBranch(text) &&
          !(text.endsWith('TO:') || COMMON_TRANSITIONS.includes(text));
        openBlock = [plainSafe ? text : `@${text}`];
        openBlockOnlyCue = true;
        lastLineOfBlockWasDialogue = false;
        break;
      }

      case 'dialogue': {
        noteTextAlterations(text);
        if (!openBlock) {
          // Orphan — Fountain dialogue is contextual; no cue, no block.
          emitTextAsActionLines(text, out, notes);
          notes.orphanDialogue += 1;
          break;
        }
        if (text === '') {
          // A blank line would END the block — unexpressible inside it.
          closeBlock();
          out.push('!');
          notes.emptyInChain += 1;
          break;
        }
        if (lastLineOfBlockWasDialogue) {
          // Reimport joins consecutive dialogue lines into ONE element.
          notes.adjacentDialogueMerged += 1;
        }
        for (const line of text.split('\n')) {
          if (line.trim() === '') {
            notes.blankLineInText += 1; // would end the block on reimport
          } else if (line.startsWith('(') && line.endsWith(')')) {
            notes.textAlteredOnReimport += 1; // would split out as parenthetical
          }
          openBlock.push(line);
        }
        openBlockOnlyCue = false;
        lastLineOfBlockWasDialogue = true;
        break;
      }

      case 'parenthetical': {
        noteTextAlterations(text);
        const wellFormed =
          !text.includes('\n') && text.startsWith('(') && text.endsWith(')') && text.length >= 2;
        if (!openBlock) {
          emitTextAsActionLines(text, out, notes);
          notes.orphanParentheticals += 1;
          break;
        }
        if (!wellFormed) {
          // Inside a block a non-"(…)" line reads as dialogue; outside it,
          // as action. Either way the type degrades — emit outside so the
          // degradation is at least consistent (action) and countable.
          closeBlock();
          emitTextAsActionLines(text, out, notes);
          notes.malformedParentheticals += 1;
          break;
        }
        openBlock.push(text);
        openBlockOnlyCue = false;
        lastLineOfBlockWasDialogue = false;
        break;
      }

      default:
        throw new TypeError(`serializeFountain: unknown element type "${el.type}"`);
    }
  }
  closeBlock();

  // With no title page emitted, a first line shaped "Notes: …" / "Draft: …"
  // (any KNOWN title key) would be captured INTO the title page on reimport
  // — the element would vanish from the parsed body entirely, the one
  // outcome this module must never allow. Force-mark just that line.
  if (titleLines.length === 0 && out.length > 0) {
    const firstPara = out[0].split('\n');
    const m = /^([A-Za-z][A-Za-z0-9 ]*):\s*(.*)$/.exec(firstPara[0]);
    if (m && KNOWN_TITLE_KEYS.has(m[1].trim().toLowerCase())) {
      // out[0] always originates from elements[0] (a cue's paragraph is
      // only pushed at closeBlock, which a later element triggers — order
      // preserved). If that element wasn't an action, the retro-"!" flips
      // its TYPE on reimport (e.g. a transition "NOTES: CUT TO:") — count
      // it, never flip silently (adversarial F2).
      if (elements[0].type !== 'action' && elements[0].type !== 'shot') {
        notes.textAlteredOnReimport += 1;
      }
      if (firstPara.length === 1) {
        out[0] = `!${out[0]}`;
      } else {
        // "!" forces only one line — the paragraph splits on reimport.
        out[0] = `!${firstPara[0]}\n\n${firstPara.slice(1).join('\n')}`;
        notes.actionSplitByCollision += 1;
      }
    }
  }

  // Cross-element note/boneyard pairs (adversarial F1): the parser strips
  // these over the WHOLE source before line-splitting, so a "[[" in one
  // element pairing with a "]]" in a LATER element deletes everything
  // between them on reimport — invisible to the per-element check above.
  // Count pairs in the final text against pairs found within individual
  // elements; any surplus formed across boundaries.
  const finalText = out.join('\n\n');
  for (const re of [NOTE_PAIR_RE, BONEYARD_PAIR_RE]) {
    const whole = countPairs(finalText, re);
    const within = elements.reduce((a, el) => a + countPairs(el.text || '', re), 0);
    if (whole > within) notes.textAlteredOnReimport += whole - within;
  }

  return { text: finalText, notes };
}

// Mirror of fountain.js's KNOWN_TITLE_KEYS (matched pair — the round-trip
// suite pins it via the first-line collision test).
const KNOWN_TITLE_KEYS = new Set([
  'title', 'credit', 'author', 'authors', 'source', 'draft date',
  'contact', 'contact info', 'notes', 'copyright', 'revision', 'draft',
]);

/**
 * Download-safe filename stem for exports. ⚠ MATCHED PAIR with the server's
 * safe_name regex in export_screenplay (server.py) — the .fountain filename
 * and the PDF filename must agree for the same title. Pinned by a parity
 * test in fountainSerializer.test.js.
 */
export function screenplaySafeName(title) {
  return (
    (title || 'Screenplay').replace(/[^a-zA-Z0-9]+/g, '_').replace(/^_+|_+$/g, '') ||
    'Screenplay'
  );
}

/** Does this single line hit any cascade branch BEFORE the transition /
 * character heuristics? (Used to decide whether a plain cue/transition
 * emission is safe.) */
function plainStartsAsEarlierBranch(line) {
  return (
    /^={3,}$/.test(line) ||
    /^#{1,6}(\s|$)/.test(line) ||
    line.startsWith('=') ||
    (line.startsWith('.') && line[1] !== '.') ||
    HEADING_PREFIX_RE.test(line) ||
    line.startsWith('>') ||
    line.startsWith('@') ||
    line.startsWith('!') ||
    line.startsWith('~')
  );
}

/**
 * Emit arbitrary element text as action, preserving every character.
 * Single-line safe text emits plain (default branch). A colliding first
 * line takes the "!" force marker — but "!" only forces ONE line, so a
 * multi-line text with a colliding first line unavoidably reimports as two
 * elements (counted). Embedded blank lines split the element (counted).
 */
function emitTextAsActionLines(text, out, notes) {
  const lines = text.split('\n');
  const paragraphHasBlank = lines.some((l) => l.trim() === '');
  if (paragraphHasBlank) notes.blankLineInText += 1;

  const first = lines[0];
  // Continuation lines never need checks — the default-action branch joins
  // every following non-blank line regardless of shape. The cue heuristic's
  // lookahead is the IMMEDIATE next line: for a single-line paragraph that
  // is the blank separator (never a cue), for a multi-line paragraph it is
  // the paragraph's own second line.
  const followerNonBlank = lines.length > 1 && lines[1].trim() !== '';
  if (!plainLineMisclassifies(first, followerNonBlank)) {
    out.push(text);
    return;
  }
  if (lines.length === 1) {
    out.push(`!${text}`);
    return;
  }
  // Colliding first line + continuation: "!" breaks the join. Two elements
  // on reimport — text survives, boundary degrades.
  out.push(`!${first}`);
  out.push(lines.slice(1).join('\n'));
  notes.actionSplitByCollision += 1;
}
